import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PhoneCall } from 'lucide-react';
import { courierAdvanceActiveDelivery, courierGetActiveDelivery, courierPushLiveLocation } from '../utils/courierAPI';
import { useAuth } from '../contexts/AuthContext';

export default function CourierPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isCourier, isLoading, logout } = useAuth();
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);
  const lastPushAtRef = useRef(null);

  const phoneHref = useMemo(() => {
    const raw = (active?.delivery?.recipient_phone || '').trim();
    const digits = raw.replace(/[^0-9+]/g, '');
    if (!digits) return null;
    return `tel:${digits}`;
  }, [active?.delivery?.recipient_phone]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  async function loadActive() {
    try {
      const data = await courierGetActiveDelivery();
      const next = data?.active || null;
      setActive(next);
      return next;
    } catch (e) {
      setActive(null);
      throw e;
    }
  }

  useEffect(() => {
    if (!isLoading && isAuthenticated && isCourier) {
      loadActive().catch(() => {
        toast.error('Teslimat bilgisi alınamadı');
      });
    }
  }, [isLoading, isAuthenticated, isCourier]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-10 px-4">
        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="text-sm text-gray-600">Yükleniyor…</div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 py-10 px-4">
        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="text-sm text-gray-700">Bu sayfayı kullanmak için giriş yapmalısın.</div>
          </div>
        </div>
      </div>
    );
  }

  if (!isCourier) {
    return (
      <div className="min-h-screen bg-gray-50 py-10 px-4">
        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="text-sm text-gray-700">Kurye yetkin yok. Lütfen doğru kurye hesabı ile giriş yap.</div>
          </div>
        </div>
      </div>
    );
  }

  if (!active) {
    return (
      <div className="min-h-screen bg-gray-50 py-10 px-4">
        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="font-manrope font-bold text-2xl text-gray-900">Kurye Paneli</h1>
              </div>
              <button
                onClick={onLogout}
                className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
              >
                Çıkış Yap
              </button>
            </div>

            <div className="mt-6 text-sm text-gray-700">
              Şu anda sana atanmış aktif bir teslimat yok
            </div>
          </div>
        </div>
      </div>
    );
  }

  async function pushOnce(orderCode) {
    if (!navigator.geolocation) {
      return;
    }

    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      });
    });

    const payload = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      heading: position.coords.heading,
      speed: position.coords.speed,
      source: 'courier_web',
    };

    const res = await courierPushLiveLocation(orderCode.trim(), payload);
    lastPushAtRef.current = res?.stored_at || new Date().toISOString();
  }

  function stopTracking() {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }

  async function startTracking(orderCode) {
    stopTracking();

    try {
      await pushOnce(orderCode);
    } catch (e) {
      // silently ignore - courier shouldn't deal with technical errors
    }

    timerRef.current = setInterval(async () => {
      try {
        await pushOnce(orderCode);
      } catch (e) {
        // silently ignore
      }
    }, 12000);
  }

  async function onLogout() {
    try {
      stopTracking();
    } catch (e) {
      // ignore
    }
    try {
      await logout();
      toast.success('Çıkış yapıldı');
      navigate('/giris');
    } catch (e) {
      toast.error('Çıkış yapılamadı');
    }
  }

  async function onPrimaryAction() {
    if (!active?.action_label) return;
    if (busy) return;

    setBusy(true);
    try {
      const res = await courierAdvanceActiveDelivery(active.action_label);
      const next = {
        ...(active || {}),
        courier_state: res?.courier_state || active?.courier_state,
        delivery_status_text: res?.delivery_status_text || active?.delivery_status_text,
        action_label: res?.action_label,
        should_track_location: !!res?.should_track_location,
      };

      setActive(next);

      const oc = (next?.order_code || '').trim();
      if (oc && next?.should_track_location) {
        await startTracking(oc);
      } else {
        stopTracking();
      }

      if ((next?.courier_state || '').toLowerCase() === 'teslim_edildi') {
        toast.success('Teslimat tamamlandı');
        await loadActive();
      }
    } catch (e) {
      toast.error('İşlem yapılamadı');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="font-manrope font-bold text-2xl text-gray-900">Kurye Paneli</h1>
            </div>
            <button
              onClick={onLogout}
              className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
            >
              Çıkış Yap
            </button>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4">
            <div className="rounded-xl border border-gray-200 p-4">
              <div className="text-xs text-gray-600">Sipariş Kodu</div>
              <div className="mt-1 text-base font-semibold text-gray-900">{active.order_code}</div>
            </div>

            <div className="rounded-xl border border-gray-200 p-4">
              <div className="text-xs text-gray-600">Alım Noktası</div>
              <div className="mt-1 text-sm font-semibold text-gray-900">{active?.pickup?.name || '-'}</div>
              <div className="mt-1 text-sm text-gray-700">{active?.pickup?.address || '-'}</div>
            </div>

            <div className="rounded-xl border border-gray-200 p-4">
              <div className="text-xs text-gray-600">Teslimat Adresi</div>
              <div className="mt-1 text-sm text-gray-900">{active?.delivery?.address || '-'}</div>
              {!!active?.navigation_url && (
                <div className="mt-3">
                  <a
                    href={active.navigation_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold border border-gray-200 text-gray-800 hover:bg-gray-50"
                  >
                    Yol Tarifi
                  </a>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-gray-200 p-4">
              <div className="text-xs text-gray-600">Alıcı</div>
              <div className="mt-1 text-sm font-semibold text-gray-900">{active?.delivery?.recipient_name || '-'}</div>
              <div className="mt-1">
                <a
                  href={phoneHref || '#'}
                  onClick={(e) => {
                    if (!phoneHref) e.preventDefault();
                  }}
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold border ${phoneHref ? 'border-gray-200 text-gray-800 hover:bg-gray-50' : 'border-gray-100 text-gray-400 cursor-not-allowed'}`}
                >
                  <PhoneCall className="w-4 h-4" />
                  Alıcıyı Ara
                </a>
              </div>
            </div>

            <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
              <div className="text-xs text-gray-600">Teslimat Durumu</div>
              <div className="mt-1 text-sm font-semibold text-gray-900">{active?.delivery_status_text || '-'}</div>
            </div>

            {active?.action_label && (
              <button
                onClick={onPrimaryAction}
                disabled={busy}
                className="w-full rounded-xl bg-trendyol-orange text-white py-3 font-semibold hover:opacity-95 disabled:opacity-50"
              >
                {active.action_label}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
